import type { FC, ReactNode } from 'react'

import { CircleCheck } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SkyPanel } from '@/components/generals/sky-panel'
import { Foliage } from '@/components/landing/foliage'
import { ContactForm } from '@/components/contact/contact-form'

const EXPECTATIONS = [
    'A live run on a sample contract from your state',
    'Straight answers about fit, setup and pricing',
    'No pressure, and no endless follow-up emails'
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description='Book a walkthrough of Closewell on a sample contract from your state, or ask us anything about pricing and fit.'
            />

            <section aria-labelledby='contact-title' className='relative px-2 pt-6 pb-20 sm:px-4 sm:pt-10 lg:px-5'>
                <Foliage side='right' className='-top-6 right-0 w-[min(26vw,400px)]' />

                <SkyPanel preload innerClassName='mx-auto grid max-w-6xl items-start gap-10 px-4 py-12 sm:px-8 sm:py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,33rem)] lg:gap-16 lg:py-24'>
                    <div className='lg:pt-8'>
                        <h1 id='contact-title' className='animate-in font-heading text-[clamp(3rem,6vw,5rem)] leading-[0.95] tracking-[-0.03em] text-foreground duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] fade-in blur-in-8 slide-in-from-bottom-8'>
                            Book a walkthrough,
                            <span className='block italic'>or just say hello</span>
                        </h1>

                        <p className='mt-6 max-w-md animate-in text-[17px] leading-relaxed text-foreground/70 delay-300 duration-1000 fill-mode-both fade-in slide-in-from-bottom-3'>
                            Tell us a little about how you work. We will set up a 30 minute walkthrough on a sample contract and show you exactly what Closewell would take off your plate.
                        </p>

                        <ul className='mt-8 space-y-3'>
                            {EXPECTATIONS.map((item, index) => (
                                <li
                                    key={item}
                                    className='flex animate-in items-start gap-3 text-[15px] text-foreground/85 duration-700 fill-mode-both fade-in slide-in-from-left-4'
                                    style={{ animationDelay: `${550 + index * 140}ms` }}
                                >
                                    <CircleCheck className='mt-0.5 size-5 shrink-0 text-leaf' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='animate-in rounded-2xl border border-white/70 bg-paper/95 p-5 shadow-[0_40px_80px_-40px_rgba(38,56,92,0.55)] delay-200 duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both fade-in zoom-in-95 slide-in-from-bottom-12 sm:p-8'>
                        <ContactForm />
                    </div>
                </SkyPanel>
            </section>
        </Layout>
    )
}

export default Contact
